import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { updateError } from './user';
import { dispatch, store } from '../store';

// State shape:
// {
//   loading: bool,
//   byPractice: {
//     [practiceId]: {
//       byParent: { [parentTable]: [tab, ...] },   // tabs grouped by their parent_table
//       byId:     { [tabId]: tab },                // flat lookup for selectors
//     }
//   }
// }
const initialState = {
    loading: false,
    byPractice: {},
};

// One monotonic sequence shared by all load calls. Only the latest selected
// practice request may write tab state, global loading, or global error UI.
// Deliberately no production reset hook: request identities remain monotonic
// for the lifetime of the module.
let nextLoadRequestId = 0;
let latestLoadRequestId = 0;

const indexTabs = (tabs) => {
    const byParent = {};
    const byId = {};
    tabs.forEach((tab) => {
        if (!byParent[tab.parent_table]) byParent[tab.parent_table] = [];
        byParent[tab.parent_table].push(tab);
        byId[tab.id] = tab;
    });
    // Sort each parent's tabs by position to match backend ordering
    Object.keys(byParent).forEach((p) => {
        byParent[p].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    });
    return { byParent, byId };
};

const slice = createSlice({
    name: 'customTableTabs',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setForPractice(state, action) {
            const { practiceId, tabs } = action.payload;
            state.byPractice[practiceId] = indexTabs(tabs || []);
        },
        upsertTab(state, action) {
            const { practiceId, tab } = action.payload;
            const slot = state.byPractice[practiceId] || { byParent: {}, byId: {} };
            slot.byId[tab.id] = tab;
            const list = slot.byParent[tab.parent_table] || [];
            const idx = list.findIndex((t) => t.id === tab.id);
            if (idx >= 0) list[idx] = tab; else list.push(tab);
            list.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
            slot.byParent[tab.parent_table] = list;
            state.byPractice[practiceId] = slot;
        },
        removeTab(state, action) {
            const { practiceId, tabId } = action.payload;
            const slot = state.byPractice[practiceId];
            if (!slot) return;
            const tab = slot.byId[tabId];
            if (!tab) return;
            delete slot.byId[tabId];
            const list = (slot.byParent[tab.parent_table] || []).filter((t) => t.id !== tabId);
            slot.byParent[tab.parent_table] = list;
        },
        resetCustomTableTabs(state) {
            Object.assign(state, initialState);
        },
    },
});

export default slice.reducer;
export const {
    setLoading, setForPractice, upsertTab, removeTab, resetCustomTableTabs,
} = slice.actions;

// ---------- Selectors ----------

// Stable empty array — prevents `|| []` from returning a new reference on each
// call when there are no tabs, which would break shallowEqual comparisons in
// useSelector and trigger re-renders on every store update.
const EMPTY_TABS = [];

export const selectCustomTabsForParent = (state, practiceId, parentTable) =>
    state.customTableTabs.byPractice[practiceId]?.byParent?.[parentTable] || EMPTY_TABS;

export const selectCustomTabById = (state, practiceId, tabId) =>
    state.customTableTabs.byPractice[practiceId]?.byId?.[tabId] || null;

// Returns all custom tabs for a practice grouped by parentTable: { [parentTable]: [tab, ...] }
// Stable empty object sentinel to avoid unnecessary re-renders when there are no tabs.
const EMPTY_BY_PARENT = {};
export const selectAllCustomTabsByParent = (state, practiceId) =>
    state.customTableTabs.byPractice[practiceId]?.byParent || EMPTY_BY_PARENT;

// ---------- Thunks ----------

export function loadCustomTabs(practiceId) {
    return async () => {
        if (!practiceId) return;
        nextLoadRequestId += 1;
        const requestId = nextLoadRequestId;
        latestLoadRequestId = requestId;
        const isCurrentRequest = () => latestLoadRequestId === requestId;
        dispatch(setLoading(true));
        try {
            const res = await callApi(`/api/v1/practices/${practiceId}/custom-table-tabs/?limit=1000`);
            if (res.status === 200 && isCurrentRequest()) {
                const tabs = res.data?.results || res.data || [];
                dispatch(setForPractice({ practiceId, tabs }));
            }
        } catch (err) {
            if (isCurrentRequest()) {
                dispatch(updateError({ success: false, message: 'Failed to load custom table tabs' }));
            }
        } finally {
            if (isCurrentRequest()) dispatch(setLoading(false));
        }
    };
}

export function saveCustomTab({ practiceId, body, tabId }) {
    return async () => {
        if (!practiceId) return null;
        const url = tabId
            ? `/api/v1/practices/${practiceId}/custom-table-tabs/${tabId}/`
            : `/api/v1/practices/${practiceId}/custom-table-tabs/`;
        const method = tabId ? 'patch' : 'post';
        try {
            const res = await callApi(url, method, body);
            if (res.status >= 200 && res.status < 300) {
                dispatch(upsertTab({ practiceId, tab: res.data }));
                return res.data;
            }
            dispatch(updateError({ success: false, message: res.data?.detail || 'Failed to save custom tab' }));
            return null;
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Failed to save custom tab' }));
            return null;
        }
    };
}

export function deleteCustomTab({ practiceId, tabId }) {
    return async () => {
        if (!practiceId || !tabId) return;
        try {
            const res = await callApi(`/api/v1/practices/${practiceId}/custom-table-tabs/${tabId}/`, 'delete');
            if (res.status === 204 || res.status === 200) {
                dispatch(removeTab({ practiceId, tabId }));
            } else {
                dispatch(updateError({ success: false, message: res.data?.detail || 'Failed to delete custom tab' }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Failed to delete custom tab' }));
        }
    };
}

export function reorderCustomTabs({ practiceId, parentTable, orderedIds }) {
    // Match the pattern of the other thunks in this file: plain async with no
    // injected args, closing over the imported `dispatch`/`store`. An earlier
    // version took `(getState)` as an arg, but redux-thunk passes `(dispatch,
    // getState)` so the param actually received `dispatch` and `getState?.()`
    // invoked `dispatch()` — which throws and silently failed the local re-order.
    return async () => {
        if (!practiceId || !orderedIds?.length) return;
        const body = orderedIds.map((id, idx) => ({ id, position: idx }));
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/custom-table-tabs/reorder/`,
                'post',
                body,
            );
            if (res.status === 200) {
                const slot = store.getState().customTableTabs?.byPractice?.[practiceId];
                if (slot) {
                    const list = (slot.byParent[parentTable] || []).slice().sort(
                        (a, b) => orderedIds.indexOf(a.id) - orderedIds.indexOf(b.id),
                    ).map((t, idx) => ({ ...t, position: idx }));
                    list.forEach((t) => dispatch(upsertTab({ practiceId, tab: t })));
                }
            } else {
                dispatch(updateError({ success: false, message: 'Failed to reorder custom tabs' }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Failed to reorder custom tabs' }));
        }
    };
}
